/**
 * FSA-2.4 CF4 — Working Details modal for a single Cash Flow subsection
 * (Adjustments / Working Capital / Investing / Financing).
 *
 * Each subsection holds an ordered list of items. Each item is one CF
 * row: Depreciation, Interest Expense, Δ Trade Receivables, etc. Items
 * can be:
 *   • Auto-derived (value_source set on a seeded item)  → auto value rolls in live
 *   • Auto + workings (auto + Σ adjustments)            → composed final
 *   • Override (auditor-typed final wins)               → manual override
 *   • Pure manual (no value_source)                     → just a number
 *
 * Composition rules mirror the backend (`ApplyCfComposition`):
 *   final = is_override ? typed : (auto + Σ workings.adjustments)
 *
 * The modal updates an internal working copy of the items list and
 * pushes it back via onSave when the auditor commits. The parent
 * Statement screen merges the items into workspace.subsections[code]
 * and the existing Save flow persists.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { autoRound } from './notes/shared/rounding';
import type {
  EvaluatedSubsectionItem,
  SubsectionItemData,
  StatementLineWorkings,
  StatementLineWorkingAdjustment,
} from 'api/types/fsa/statementData.types';

interface Props {
  isOpen: boolean;
  subsectionCode: string;
  subsectionLabel: string;
  /** Live-evaluated items from EvaluatedLine.subsectionItems (carries fresh autoValueCy). */
  evaluatedItems: EvaluatedSubsectionItem[];
  /** Existing saved items from workspace.subsections[code].items. Empty when never saved. */
  savedItems: SubsectionItemData[];
  displayMode: 'actual' | 'rounded';
  /** Engagement rounding factor — workings adjustments are rupees, converted for the rounded final. */
  roundingFactorKey?: string | null;
  onSave: (items: SubsectionItemData[]) => void;
  onClose: () => void;
}

/** Local row state — combines saved snapshot + fresh auto eval, drives every input. */
interface RowState {
  id: string;
  label: string;
  order: number;
  /** Final CY actual — read from saved; recomputed on the fly when auto + no override + no workings. */
  amountActual: number;
  amountRounded: number;
  priorAmountActual: number;
  priorAmountRounded: number;
  valueSource?: unknown;
  isOverride: boolean;
  /** Fresh auto value resolved by backend — read-only display. */
  autoValueCy?: number | null;
  autoValueCyRounded?: number | null;
  autoValueSourceType?: string | null;
  workings?: StatementLineWorkings | null;
  /** UI-only — true when the auditor has the per-item workings drawer open. */
  workingsOpen: boolean;
}

const SOURCE_LABEL: Record<string, string> = {
  bs_movement:    'from BS Δ',
  note_total:     'from Note',
  note_class:     'from Note class',
  column_sum:     'from column',
  statement_line: 'from PL',
  sum_of:         'composed',
};

const FsaCfSubsectionWorkingsModal: React.FC<Props> = ({
  isOpen,
  subsectionCode,
  subsectionLabel,
  evaluatedItems,
  savedItems,
  displayMode,
  roundingFactorKey,
  onSave,
  onClose,
}) => {
  const [rows, setRows] = useState<RowState[]>([]);
  const initialSnapshotRef = useRef('');
  const [discardOpen, setDiscardOpen] = useState(false);

  // Hydrate row state on open. Prefer saved items (auditor's edits)
  // and merge in the evaluator's fresh autoValueCy by id so we always
  // show the latest source value alongside the saved final.
  useEffect(() => {
    if (!isOpen) return;
    const evalById = new Map(evaluatedItems.map((e) => [e.id, e]));
    const merged: RowState[] = savedItems.length > 0
      ? savedItems.map((s) => {
          const e = evalById.get(s.id);
          return {
            id: s.id,
            label: s.label,
            order: s.order ?? 0,
            amountActual: s.amount_actual,
            amountRounded: s.amount_rounded,
            priorAmountActual: s.prior_amount_actual,
            priorAmountRounded: s.prior_amount_rounded,
            valueSource: s.value_source,
            isOverride: s.is_override,
            autoValueCy: e?.autoValueCy ?? s.auto_value_cy,
            autoValueCyRounded: e?.autoValueCyRounded ?? s.auto_value_cy_rounded,
            autoValueSourceType: e?.autoValueSourceType,
            workings: s.workings,
            workingsOpen: false,
          };
        })
      : evaluatedItems.map((e) => ({
          id: e.id,
          label: e.label,
          order: e.order,
          amountActual: e.amountActual,
          amountRounded: e.amountRounded,
          priorAmountActual: e.priorAmountActual,
          priorAmountRounded: e.priorAmountRounded,
          isOverride: e.isOverride,
          autoValueCy: e.autoValueCy,
          autoValueCyRounded: e.autoValueCyRounded,
          autoValueSourceType: e.autoValueSourceType,
          workingsOpen: false,
        }));
    merged.sort((a, b) => a.order - b.order);
    setRows(merged);
    initialSnapshotRef.current = JSON.stringify(merged);
  }, [isOpen, savedItems, evaluatedItems]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(rows) !== initialSnapshotRef.current;
  }, [rows]);

  // Compose final per row — same rules as backend ApplyCfComposition.
  const composeFinal = useCallback((r: RowState): { cy: number; cyRounded: number } => {
    if (r.isOverride) return { cy: r.amountActual, cyRounded: r.amountRounded };
    const sumAdj = (r.workings?.adjustments ?? []).reduce((s, a) => s + (a.amount || 0), 0);
    if (r.workings) {
      return {
        cy: (r.autoValueCy ?? 0) + sumAdj,
        // Adjustments are rupees — convert to the rounded units (₹50,000 →
        // 50.00 under THOUSANDS), same as the backend's RoundedAdjustment.
        cyRounded: (r.autoValueCyRounded ?? 0) + autoRound(sumAdj, roundingFactorKey),
      };
    }
    if (r.autoValueCy != null) return { cy: r.autoValueCy, cyRounded: r.autoValueCyRounded ?? 0 };
    return { cy: r.amountActual, cyRounded: r.amountRounded };
  }, [roundingFactorKey]);

  const subtotal = useMemo(
    () => rows.reduce((sum, r) => sum + composeFinal(r).cy, 0),
    [rows, composeFinal],
  );
  const priorSubtotal = useMemo(
    () => rows.reduce((sum, r) => sum + (r.priorAmountActual || 0), 0),
    [rows],
  );

  const updateRow = useCallback(<K extends keyof RowState>(id: string, field: K, value: RowState[K]) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  }, []);

  const updateWorkings = useCallback((id: string, updater: (w: StatementLineWorkings) => StatementLineWorkings) => {
    setRows((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      const current: StatementLineWorkings = r.workings ?? { narrative: null, adjustments: [] };
      return { ...r, workings: updater(current) };
    }));
  }, []);

  const addAdjustment = useCallback((id: string) => {
    updateWorkings(id, (w) => ({
      ...w,
      adjustments: [
        ...(w.adjustments ?? []),
        {
          id: `adj-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
          description: '',
          amount: 0,
        },
      ],
    }));
  }, [updateWorkings]);

  const removeAdjustment = useCallback((id: string, adjId: string) => {
    updateWorkings(id, (w) => ({
      ...w,
      adjustments: (w.adjustments ?? []).filter((a) => a.id !== adjId),
    }));
  }, [updateWorkings]);

  const updateAdjustment = useCallback(
    (id: string, adjId: string, patch: Partial<StatementLineWorkingAdjustment>) => {
      updateWorkings(id, (w) => ({
        ...w,
        adjustments: (w.adjustments ?? []).map((a) => (a.id === adjId ? { ...a, ...patch } : a)),
      }));
    },
    [updateWorkings],
  );

  const addItem = useCallback(() => {
    const newId = `item-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    setRows((prev) => [
      ...prev,
      {
        id: newId,
        label: '',
        order: prev.length,
        amountActual: 0,
        amountRounded: 0,
        priorAmountActual: 0,
        priorAmountRounded: 0,
        isOverride: false,
        workingsOpen: false,
      },
    ]);
  }, []);

  const removeItem = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleSave = useCallback(() => {
    // Validate — every row needs a label.
    const blank = rows.find((r) => !r.label.trim());
    if (blank) {
      notificationService.toast.warning('Every item needs a description.');
      return;
    }
    // Apply composition to get final values, then build the persistable items.
    const items: SubsectionItemData[] = rows.map((r, idx) => {
      const composed = composeFinal(r);
      return {
        id: r.id,
        label: r.label.trim(),
        order: idx,
        amount_actual: composed.cy,
        amount_rounded: composed.cyRounded,
        prior_amount_actual: r.priorAmountActual,
        prior_amount_rounded: r.priorAmountRounded,
        value_source: r.valueSource,
        is_override: r.isOverride,
        auto_value_cy: r.autoValueCy ?? null,
        auto_value_cy_rounded: r.autoValueCyRounded ?? null,
        workings: r.workings ?? null,
      };
    });
    onSave(items);
  }, [rows, composeFinal, onSave]);

  if (!isOpen) return null;

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  return (
    <>
      <div className="modal-backdrop" onClick={handleClose} />
      <div className="modal-container modal-xl">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div>
            <h2 className="modal-title">Working Details — {subsectionLabel || subsectionCode}</h2>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.125rem' }}>
              Edit items, add adjustments, override final values. Saved items persist to the engagement's Cash Flow.
            </div>
          </div>
          <button className="modal-close" onClick={handleClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ paddingTop: '0.5rem' }}>
          <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                <tr>
                  <th style={th}>Item</th>
                  <th style={{ ...th, width: 110 }}>Source</th>
                  <th style={{ ...th, textAlign: 'right', width: 110 }}>Auto CY</th>
                  <th style={{ ...th, textAlign: 'center', width: 70 }}>Adj</th>
                  <th style={{ ...th, textAlign: 'center', width: 80 }}>Override</th>
                  <th style={{ ...th, textAlign: 'right', width: 130 }}>Final CY</th>
                  <th style={{ ...th, textAlign: 'right', width: 130 }}>PY</th>
                  <th style={{ ...th, width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const composed = composeFinal(r);
                  const sourceLabel = r.autoValueSourceType
                    ? SOURCE_LABEL[r.autoValueSourceType] ?? r.autoValueSourceType
                    : '—';
                  const adjCount = (r.workings?.adjustments ?? []).length;
                  const hasAuto = r.autoValueCy != null;
                  const finalEditable = r.isOverride || (!hasAuto && !r.workings);

                  return (
                    <React.Fragment key={r.id}>
                      <tr>
                        <td style={td}>
                          <input
                            type="text"
                            value={r.label}
                            onChange={(e) => updateRow(r.id, 'label', e.target.value)}
                            placeholder="Description…"
                            style={inputStyle}
                          />
                        </td>
                        <td style={{ ...td, color: hasAuto ? '#2563eb' : 'var(--text-secondary)' }}>
                          {sourceLabel}
                        </td>
                        <td style={{ ...td, textAlign: 'right', color: 'var(--text-secondary)' }}>
                          {hasAuto ? fmt(r.autoValueCy ?? 0) : '—'}
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => updateRow(r.id, 'workingsOpen', !r.workingsOpen)}
                            title={adjCount > 0 ? `${adjCount} adjustment(s) — click to edit` : 'Add workings'}
                            style={{
                              border: 'none',
                              background: r.workingsOpen ? '#fef3c7' : 'transparent',
                              cursor: 'pointer',
                              padding: '0.125rem 0.4rem',
                              borderRadius: 4,
                              fontSize: '0.75rem',
                              color: adjCount > 0 ? '#d97706' : 'var(--text-secondary)',
                            }}
                          >
                            {adjCount > 0 ? `✎ ${adjCount}` : '+'}
                          </button>
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={r.isOverride}
                            onChange={(e) => updateRow(r.id, 'isOverride', e.target.checked)}
                            title="Override the composed value with a manual figure"
                          />
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          {finalEditable ? (
                            <input
                              type="number"
                              step="0.01"
                              value={r.amountActual}
                              onChange={(e) =>
                                updateRow(r.id, 'amountActual', parseFloat(e.target.value) || 0)
                              }
                              style={{ ...inputStyle, textAlign: 'right' }}
                            />
                          ) : (
                            <span style={{ fontWeight: 500 }}>{fmt(composed.cy)}</span>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <input
                            type="number"
                            step="0.01"
                            value={r.priorAmountActual}
                            onChange={(e) =>
                              updateRow(r.id, 'priorAmountActual', parseFloat(e.target.value) || 0)
                            }
                            style={{ ...inputStyle, textAlign: 'right' }}
                          />
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => removeItem(r.id)}
                            title="Remove item"
                            style={{
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              color: '#dc2626',
                              padding: '0.125rem',
                            }}
                          >
                            <IconSystem.trash size={14} />
                          </button>
                        </td>
                      </tr>
                      {r.workingsOpen && (
                        <tr>
                          <td colSpan={8} style={{ ...td, background: '#fafafa', padding: '0.5rem 0.75rem' }}>
                            <WorkingsEditor
                              workings={r.workings ?? { narrative: null, adjustments: [] }}
                              autoValueCy={r.autoValueCy ?? null}
                              composedCy={composed.cy}
                              onChangeNarrative={(v) =>
                                updateWorkings(r.id, (w) => ({ ...w, narrative: v }))
                              }
                              onAdd={() => addAdjustment(r.id)}
                              onRemove={(adjId) => removeAdjustment(r.id, adjId)}
                              onUpdate={(adjId, patch) => updateAdjustment(r.id, adjId, patch)}
                              fmt={fmt}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ ...td, textAlign: 'center', color: 'var(--text-secondary)', padding: '1.5rem' }}>
                      No items yet — click "Add item" to start.
                    </td>
                  </tr>
                )}
                <tr style={{ background: '#f9fafb', fontWeight: 600 }}>
                  <td style={td} colSpan={5}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 400 }}>
                      Subsection total ({rows.length} item{rows.length === 1 ? '' : 's'})
                    </span>
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmt(subtotal)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmt(priorSubtotal)}</td>
                  <td style={td}></td>
                </tr>
              </tbody>
            </table>
          </div>

          <button
            type="button"
            onClick={addItem}
            style={{
              marginTop: '0.5rem',
              padding: '0.375rem 0.75rem',
              border: '1px dashed #94a3b8',
              borderRadius: 6,
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '0.8125rem',
              color: '#2563eb',
            }}
          >
            + Add item
          </button>

          <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            <div>
              <strong>Composition:</strong> override wins. Otherwise final = auto + Σ adjustments. Items
              with an auto source but no override and no workings show the live auto value.
            </div>
            <div>
              <strong>PY:</strong> manual entry only — the system has no year-before-PY balance sheet to
              derive movement. Use the toolbar's <em>Import PY from Prior CF</em> action to bulk-populate
              when a prior engagement is linked.
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}>Save</button>
        </div>
      </div>

      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes in this subsection. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
};

const WorkingsEditor: React.FC<{
  workings: StatementLineWorkings;
  autoValueCy: number | null;
  composedCy: number;
  onChangeNarrative: (v: string | null) => void;
  onAdd: () => void;
  onRemove: (adjId: string) => void;
  onUpdate: (adjId: string, patch: Partial<StatementLineWorkingAdjustment>) => void;
  fmt: (n: number) => string;
}> = ({ workings, autoValueCy, composedCy, onChangeNarrative, onAdd, onRemove, onUpdate, fmt }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 30px', gap: '0.375rem', alignItems: 'center' }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Auto-derived starting point</div>
        <div style={{ textAlign: 'right', fontWeight: 500 }}>{autoValueCy != null ? fmt(autoValueCy) : '—'}</div>
        <div></div>
      </div>
      {(workings.adjustments ?? []).map((adj) => (
        <div key={adj.id} style={{ display: 'grid', gridTemplateColumns: '1fr 110px 30px', gap: '0.375rem', alignItems: 'center' }}>
          <input
            type="text"
            value={adj.description}
            onChange={(e) => onUpdate(adj.id, { description: e.target.value })}
            placeholder="Adjustment description…"
            style={{ ...inputStyle, fontSize: '0.75rem' }}
          />
          <input
            type="number"
            step="0.01"
            value={adj.amount}
            onChange={(e) => onUpdate(adj.id, { amount: parseFloat(e.target.value) || 0 })}
            style={{ ...inputStyle, fontSize: '0.75rem', textAlign: 'right' }}
          />
          <button
            type="button"
            onClick={() => onRemove(adj.id)}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#dc2626' }}
            title="Remove adjustment"
          >
            <IconSystem.trash size={12} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={onAdd}
        style={{
          padding: '0.25rem 0.5rem',
          border: '1px dashed #cbd5e1',
          borderRadius: 4,
          background: 'transparent',
          cursor: 'pointer',
          fontSize: '0.75rem',
          color: '#2563eb',
          alignSelf: 'flex-start',
        }}
      >
        + Add adjustment
      </button>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 30px', gap: '0.375rem', alignItems: 'center', paddingTop: '0.25rem', borderTop: '1px dashed #e5e7eb' }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Composed = Auto + Σ Adjustments</div>
        <div style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(composedCy)}</div>
        <div></div>
      </div>
      <textarea
        value={workings.narrative ?? ''}
        onChange={(e) => onChangeNarrative(e.target.value || null)}
        placeholder="Narrative / assumptions / workings notes (optional)…"
        rows={2}
        style={{ ...inputStyle, fontSize: '0.75rem', resize: 'vertical' }}
      />
    </div>
  );
};

const th: React.CSSProperties = {
  padding: '0.4rem 0.5rem',
  borderBottom: '2px solid var(--border-color)',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.75rem',
  color: 'var(--text-secondary)',
};

const td: React.CSSProperties = {
  padding: '0.3rem 0.5rem',
  borderBottom: '1px solid var(--border-color)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.25rem 0.4rem',
  fontSize: '0.8125rem',
  border: '1px solid var(--form-border-color)',
  borderRadius: 4,
  background: 'var(--bg-primary)',
  color: 'var(--text-primary)',
};

export default FsaCfSubsectionWorkingsModal;
